import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { CASE_TYPES, LEAD_TYPES, canSeeFinance } from '../../lib/constants';
import { useAuth } from '../../components/LoginGate';
import FollowupsPanel from '../../components/FollowupsPanel';

// Financial fields are optional here — the server strips them for the
// staff role, so reads must tolerate `undefined`. UI that displays them
// is additionally gated behind `canSeeFinance(user?.role)` so staff
// never see £ figures (or the column headers that imply them).
interface CaseRow {
  id: number;
  case_no: string | null;
  client_name: string | null;
  file_reference: string | null;
  case_type: string | null;
  team_name: string | null;
  file_status: string | null;
  priority: string | null;
  next_deadline_date: string | null;
  fee?: number;
  total_case_value?: number;
  amount_collected?: number;
  lead_type: string | null;
  file_open_date: string | null;
  file_close_date: string | null;
  month_opened: string | null;
  vat_applicable?: number;
  vat_amount?: number;
  notes: string | null;
  client_id: number | null;
  team_id: number | null;
  follow_up_tasks: string | null;
}

interface Stage { id?: number; stage_name: string; completion_date: string | null; deadline_date?: string | null; stage_order: number; notes?: string | null; }
interface Payment { id?: number; payment_date: string | null; amount?: number; payment_method: string | null; payment_status: string | null; vat_applied?: boolean; vat_rate?: number; }
interface CaseDetail extends CaseRow { stages: Stage[]; payments: Payment[]; }
interface Team { id: number; name: string; }
interface Client { id: number; name: string; contact: string | null; client_id: string | null; }

interface CaseForm {
  client_id: string; file_reference: string; case_type: string; lead_type: string; team_id: string;
  priority: string; file_status: string; file_open_date: string; next_deadline_date: string;
  fee: string; vat_applicable: boolean; vat_amount: string; notes: string; follow_up_tasks: string;
}

const emptyCaseForm: CaseForm = {
  client_id: '', file_reference: '', case_type: '', lead_type: '', team_id: '',
  priority: 'Medium', file_status: 'Open', file_open_date: new Date().toISOString().split('T')[0],
  next_deadline_date: '', fee: '', vat_applicable: false, vat_amount: '0', notes: '', follow_up_tasks: '',
};

const STATUS_OPTIONS = ['Open', 'Closed', 'In-progress', 'In Court', 'Waiting on Client', 'Appealed', 'Dropped', 'Settled'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const PAYMENT_METHODS = ['Bank Transfer', 'Cash', 'Card', 'Cheque', 'Online'];

// Defensive formatters — tolerate undefined/null so a stripped finance
// field doesn't take down the whole render tree. (Before this guard, a
// staff user hitting the Cases list would white-screen on
// `undefined.toLocaleString()`.)
function formatCurrency(v: number | null | undefined) {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatDate(d: string | null) { return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }

function statusBadgeClass(s: string | null) {
  if (!s) return 'badge badge-gray';
  const sl = s.toLowerCase();
  if (sl === 'closed' || sl === 'settled') return 'badge badge-green';
  if (sl === 'in-progress' || sl === 'open') return 'badge badge-blue';
  if (sl === 'in court') return 'badge badge-yellow';
  if (sl === 'dropped') return 'badge badge-red';
  if (sl === 'appealed') return 'badge badge-purple';
  if (sl === 'waiting on client') return 'badge badge-yellow';
  return 'badge badge-gray';
}

function priorityBadgeClass(p: string | null) {
  if (!p) return 'badge badge-gray';
  const pl = p.toLowerCase();
  if (pl === 'high' || pl === 'urgent') return 'badge badge-red';
  if (pl === 'medium') return 'badge badge-yellow';
  if (pl === 'low') return 'badge badge-green';
  return 'badge badge-purple';
}

export default function CasesPage() {
  const { user } = useAuth();
  const showFinance = canSeeFinance(user?.role);
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTeam, setFilterTeam] = useState('');

  const [detailCase, setDetailCase] = useState<CaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'details' | 'stages' | 'payments' | 'followups'>('details');

  const [showCaseModal, setShowCaseModal] = useState(false);
  const [editingCase, setEditingCase] = useState<CaseRow | null>(null);
  const [caseForm, setCaseForm] = useState<CaseForm>(emptyCaseForm);
  const [saving, setSaving] = useState(false);

  // Stages panel state
  const [editingDeadlineId, setEditingDeadlineId] = useState<number | null>(null);
  const [editingDeadlineVal, setEditingDeadlineVal] = useState('');

  // Close file state
  const [closeFileDate, setCloseFileDate] = useState(new Date().toISOString().split('T')[0]);
  const [closingFile, setClosingFile] = useState(false);

  // Payment form state
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payMethod, setPayMethod] = useState('Bank Transfer');
  const [payStatus, setPayStatus] = useState('Paid');
  const [payVat, setPayVat] = useState(false);
  const [savingPay, setSavingPay] = useState(false);

  const [deletingCaseId, setDeletingCaseId] = useState<number | null>(null);

  const fetchCases = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (filterStatus) p.set('status', filterStatus);
    if (filterType) p.set('type', filterType);
    if (filterPriority) p.set('priority', filterPriority);
    if (filterTeam) p.set('team', filterTeam);
    apiFetch(`/api/cases?${p}`)
      .then(r => r.json())
      .then((d: CaseRow[]) => { setCases(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search, filterStatus, filterType, filterPriority, filterTeam]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => {
    apiFetch('/api/teams').then(r => r.json()).then(setTeams).catch(() => {});
    apiFetch('/api/clients').then(r => r.json()).then(setClients).catch(() => {});
  }, []);

  function openDetail(caseId: number) {
    setDetailLoading(true);
    setDetailTab('details');
    apiFetch(`/api/cases/${caseId}`)
      .then(r => r.json())
      .then((d: CaseDetail) => { setDetailCase(d); setDetailLoading(false); })
      .catch(() => setDetailLoading(false));
  }

  function refreshDetail() { if (detailCase) openDetail(detailCase.id); }
  function closeDetail() { setDetailCase(null); setDetailLoading(false); }

  function openNewModal() { setEditingCase(null); setCaseForm(emptyCaseForm); setShowCaseModal(true); }
  function openEditModal(c: CaseRow) {
    setEditingCase(c);
    setCaseForm({
      client_id: c.client_id ? String(c.client_id) : '',
      file_reference: c.file_reference || '',
      case_type: c.case_type || '',
      lead_type: c.lead_type || '',
      team_id: c.team_id ? String(c.team_id) : '',
      priority: c.priority || 'Medium',
      file_status: c.file_status || 'Open',
      file_open_date: c.file_open_date || '',
      next_deadline_date: c.next_deadline_date || '',
      fee: c.fee ? String(c.fee) : '',
      vat_applicable: c.vat_applicable === 1,
      vat_amount: c.vat_amount ? String(c.vat_amount) : '0',
      notes: c.notes || '',
      follow_up_tasks: c.follow_up_tasks || '',
    });
    setShowCaseModal(true);
  }

  function handleFormChange(field: keyof CaseForm, value: string | boolean) {
    setCaseForm(prev => {
      const u = { ...prev, [field]: value };
      // Auto-fill file_reference from selected client's file reference
      if (field === 'client_id' && value) {
        const selectedClient = clients.find(c => String(c.id) === value);
        if (selectedClient?.client_id && !prev.file_reference) {
          u.file_reference = selectedClient.client_id;
        }
      }
      if (field === 'fee' || field === 'vat_applicable') {
        const fee = parseFloat(field === 'fee' ? (value as string) : prev.fee) || 0;
        const vatApp = field === 'vat_applicable' ? (value as boolean) : prev.vat_applicable;
        u.vat_amount = vatApp ? (fee * 0.2).toFixed(2) : '0';
      }
      return u;
    });
  }

  async function handleSaveCase() {
    if (!caseForm.client_id) return;
    setSaving(true);
    const fee = parseFloat(caseForm.fee) || 0;
    const vat = parseFloat(caseForm.vat_amount) || 0;
    const payload = {
      client_id: Number(caseForm.client_id),
      file_reference: caseForm.file_reference || null,
      case_type: caseForm.case_type || null,
      lead_type: caseForm.lead_type || null,
      team_id: caseForm.team_id ? Number(caseForm.team_id) : null,
      priority: caseForm.priority || 'Medium',
      file_status: caseForm.file_status || 'Open',
      file_open_date: caseForm.file_open_date || null,
      month_opened: caseForm.file_open_date ? caseForm.file_open_date.substring(0, 7) : null,
      next_deadline_date: caseForm.next_deadline_date || null,
      fee, vat_applicable: caseForm.vat_applicable ? 1 : 0, vat_amount: vat, total_case_value: fee + vat,
      notes: caseForm.notes || null, follow_up_tasks: caseForm.follow_up_tasks || null,
    };
    try {
      if (editingCase) {
        await apiFetch(`/api/cases/${editingCase.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/cases', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      setShowCaseModal(false); setEditingCase(null); setCaseForm(emptyCaseForm);
      fetchCases();
    } finally { setSaving(false); }
  }

  async function markStageComplete(stageId: number) {
    await apiFetch(`/api/stages/${stageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ completion_date: new Date().toISOString().split('T')[0] }),
    });
    refreshDetail();
  }

  async function saveStageDeadline(stageId: number) {
    await apiFetch(`/api/stages/${stageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deadline_date: editingDeadlineVal || null }),
    });
    setEditingDeadlineId(null);
    setEditingDeadlineVal('');
    refreshDetail();
  }

  async function handleCloseFile() {
    if (!detailCase || !closeFileDate) return;
    setClosingFile(true);
    try {
      await apiFetch(`/api/cases/${detailCase.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_status: 'Closed', file_close_date: closeFileDate }),
      });
      refreshDetail();
      fetchCases();
    } finally { setClosingFile(false); }
  }

  async function handleReopenFile() {
    if (!detailCase) return;
    await apiFetch(`/api/cases/${detailCase.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_status: 'Open', file_close_date: null }),
    });
    refreshDetail();
    fetchCases();
  }

  async function handleAddPayment() {
    if (!detailCase || !payAmount) return;
    setSavingPay(true);
    try {
      const net = parseFloat(payAmount) || 0;
      const vatAmt = payVat ? parseFloat((net * 0.2).toFixed(2)) : 0;
      const total = net + vatAmt;
      await apiFetch('/api/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: detailCase.id,
          payment_date: payDate || null,
          amount: total,
          payment_method: payMethod || null,
          payment_status: payStatus,
          vat_applied: payVat,
          vat_rate: payVat ? 20 : 0,
          invoice_month: payDate ? payDate.substring(0, 7) : null,
        }),
      });
      setPayAmount(''); setPayDate(new Date().toISOString().split('T')[0]); setPayMethod('Bank Transfer'); setPayStatus('Paid'); setPayVat(false);
      refreshDetail(); fetchCases();
    } finally { setSavingPay(false); }
  }

  async function handleDeleteCase(caseId: number) {
    await apiFetch(`/api/cases/${caseId}`, { method: 'DELETE' });
    setDeletingCaseId(null);
    if (detailCase?.id === caseId) closeDetail();
    fetchCases();
  }

  async function updateCaseStatus(caseId: number, newStatus: string) {
    await apiFetch(`/api/cases/${caseId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file_status: newStatus }) });
    fetchCases();
    if (detailCase && detailCase.id === caseId) refreshDetail();
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cases</h1>
          <p className="page-subtitle">Track all immigration cases</p>
        </div>
        <button className="btn btn-primary" onClick={openNewModal}>New Case</button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input className="form-input" type="search" placeholder="Search by client or file reference..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
        <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: 1 }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-input" value={filterTeam} onChange={e => setFilterTeam(e.target.value)} style={{ flex: 1 }}>
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
        </select>
        <select className="form-input" value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ flex: 1 }}>
          <option value="">All Priorities</option>
          {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button className="btn" onClick={() => { setSearch(''); setFilterStatus(''); setFilterTeam(''); setFilterPriority(''); setFilterType(''); }}>Clear</button>
      </div>

      <div className="card">
        <div className="table-container">
          {cases.length === 0 ? (
            <div className="empty-state"><p>No cases found. Click "New Case" to add one.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>File Ref</th>
                  <th>Type</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Deadline</th>
                  <th>Closed</th>
                  {showFinance && <th>Value</th>}
                  {showFinance && <th>Collected</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(c.id)}>
                    <td style={{ fontWeight: 600 }}>{c.client_name || '-'}</td>
                    <td>{c.file_reference || '-'}</td>
                    <td>{c.case_type || '-'}</td>
                    <td>{c.team_name || '-'}</td>
                    <td><span className={statusBadgeClass(c.file_status)}>{c.file_status}</span></td>
                    <td><span className={priorityBadgeClass(c.priority)}>{c.priority}</span></td>
                    <td style={c.next_deadline_date ? { color: 'var(--warning)' } : {}}>{formatDate(c.next_deadline_date)}</td>
                    <td style={{ fontSize: 12 }}>{c.file_close_date ? <span className="badge badge-gray">{formatDate(c.file_close_date)}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}</td>
                    {showFinance && <td>{formatCurrency(c.total_case_value)}</td>}
                    {showFinance && <td>{formatCurrency(c.amount_collected)}</td>}
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-icon" onClick={() => openEditModal(c)}>Edit</button>
                      <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => setDeletingCaseId(c.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Case Detail Drawer */}
      {(detailCase || detailLoading) && (
        <div className="modal-overlay" onClick={closeDetail}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            {detailLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>
            ) : detailCase && (
              <>
                <div className="modal-header">
                  <div>
                    <h2>{detailCase.client_name}</h2>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {detailCase.file_reference} · {detailCase.case_type}
                      {detailCase.file_close_date && (
                        <span className="badge badge-green" style={{ marginLeft: 8 }}>Closed {formatDate(detailCase.file_close_date)}</span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="form-input" value={detailCase.file_status || 'Open'} onChange={e => updateCaseStatus(detailCase.id, e.target.value)} style={{ fontSize: 13 }}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => setDeletingCaseId(detailCase.id)} title="Delete case">🗑</button>
                    <button className="btn-icon" onClick={closeDetail}>✕</button>
                  </div>
                </div>

                <div className="tabs" style={{ padding: '0 20px' }}>
                  {(
                    showFinance
                      ? (['details', 'stages', 'followups', 'payments'] as const)
                      : (['details', 'stages', 'followups'] as const)
                  ).map(t => (
                    <button key={t} className={`tab${detailTab === t ? ' active' : ''}`} onClick={() => setDetailTab(t)}>
                      {t === 'followups' ? 'Follow-ups' : t.charAt(0).toUpperCase() + t.slice(1)}
                      {t === 'stages' && ` (${detailCase.stages?.length || 0})`}
                      {t === 'payments' && ` (${detailCase.payments?.length || 0})`}
                    </button>
                  ))}
                </div>

                <div className="modal-body">
                  {detailTab === 'details' && (
                    <div className="detail-grid">
                      {[
                        ['Client', detailCase.client_name],
                        ['File Reference', detailCase.file_reference],
                        ['Case Type', detailCase.case_type],
                        ['Team', detailCase.team_name],
                        ['Priority', detailCase.priority],
                        ['Status', detailCase.file_status],
                        ['Lead Type', detailCase.lead_type],
                        ['File Open Date', formatDate(detailCase.file_open_date)],
                        ['File Close Date', formatDate(detailCase.file_close_date)],
                        ['Deadline', formatDate(detailCase.next_deadline_date)],
                        // Financial rows only for owner / owner_2 — the
                        // server strips these fields for staff, and the
                        // arithmetic (total - collected) would otherwise
                        // be NaN.
                        ...(showFinance
                          ? ([
                              ['Fee', formatCurrency(detailCase.fee)],
                              ['VAT', formatCurrency(detailCase.vat_amount)],
                              ['Total Value', formatCurrency(detailCase.total_case_value)],
                              ['Amount Collected', formatCurrency(detailCase.amount_collected)],
                              ['Outstanding', formatCurrency((detailCase.total_case_value ?? 0) - (detailCase.amount_collected ?? 0))],
                            ] as [string, string | null][])
                          : []),
                      ].map(([label, value]) => (
                        <div key={label} className="detail-row">
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || '-'}</span>
                        </div>
                      ))}
                      {detailCase.notes && (
                        <div className="detail-row detail-row-full">
                          <span className="detail-label">Notes</span>
                          <span className="detail-value">{detailCase.notes}</span>
                        </div>
                      )}
                      {detailCase.follow_up_tasks && (
                        <div className="detail-row detail-row-full">
                          <span className="detail-label">Follow-up Tasks</span>
                          <span className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>{detailCase.follow_up_tasks}</span>
                        </div>
                      )}
                      <div className="detail-row detail-row-full" style={{ marginTop: 12 }}>
                        <button className="btn btn-secondary" onClick={() => { closeDetail(); openEditModal(detailCase); }}>Edit Case</button>
                      </div>
                    </div>
                  )}

                  {detailTab === 'stages' && (
                    <>
                      <div className="timeline">
                        {(detailCase.stages || []).map((stage, i) => (
                          <div key={i} className="timeline-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div className="timeline-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                  width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                                  background: stage.completion_date ? 'var(--success)' : 'var(--border)',
                                  color: stage.completion_date ? '#fff' : 'var(--text-muted)',
                                }}>{i + 1}</span>
                                {stage.stage_name}
                              </div>
                              <div className="timeline-date" style={{ marginLeft: 28 }}>
                                {stage.completion_date
                                  ? <span style={{ color: 'var(--success)' }}>✓ Completed {formatDate(stage.completion_date)}</span>
                                  : <span style={{ color: 'var(--text-muted)' }}>In progress</span>}
                              </div>
                              {stage.deadline_date && (
                                <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 2, marginLeft: 28 }}>
                                  ⏰ Deadline: {formatDate(stage.deadline_date)}
                                </div>
                              )}
                              {editingDeadlineId === stage.id ? (
                                <div style={{ display: 'flex', gap: 6, marginTop: 6, marginLeft: 28, alignItems: 'center' }}>
                                  <input className="form-input" type="date" value={editingDeadlineVal} onChange={e => setEditingDeadlineVal(e.target.value)} style={{ fontSize: 12, padding: '3px 6px', height: 'auto' }} />
                                  <button className="btn btn-sm btn-primary" onClick={() => saveStageDeadline(stage.id!)}>Save</button>
                                  <button className="btn btn-sm" onClick={() => { setEditingDeadlineId(null); setEditingDeadlineVal(''); }}>Cancel</button>
                                </div>
                              ) : stage.id ? (
                                <button
                                  style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', marginTop: 2, marginLeft: 28 }}
                                  onClick={() => { setEditingDeadlineId(stage.id!); setEditingDeadlineVal(stage.deadline_date || ''); }}
                                >
                                  {stage.deadline_date ? 'Edit Deadline' : '+ Set Deadline'}
                                </button>
                              ) : null}
                            </div>
                            {!stage.completion_date && stage.id && (
                              <button className="btn btn-sm btn-secondary" style={{ flexShrink: 0 }} onClick={() => markStageComplete(stage.id!)}>Mark Done</button>
                            )}
                          </div>
                        ))}
                        {(detailCase.stages || []).length === 0 && (
                          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>No stages added yet</div>
                        )}
                      </div>

                      {/* File Close Section */}
                      <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                        {detailCase.file_close_date ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(34,197,94,0.08)', borderRadius: 8, padding: '12px 16px' }}>
                            <span style={{ fontSize: 20 }}>📁</span>
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--success)' }}>File Closed</div>
                              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Closed on {formatDate(detailCase.file_close_date)}</div>
                            </div>
                            <button className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={handleReopenFile}>Reopen</button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Close this File</div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <label className="form-label" style={{ margin: 0 }}>Date file closed</label>
                                <input
                                  className="form-input"
                                  type="date"
                                  value={closeFileDate}
                                  onChange={e => setCloseFileDate(e.target.value)}
                                  style={{ width: 180 }}
                                />
                              </div>
                              <button
                                className="btn btn-primary"
                                style={{ marginTop: 20 }}
                                onClick={handleCloseFile}
                                disabled={closingFile || !closeFileDate}
                              >
                                {closingFile ? 'Closing...' : '📁 Mark as Closed'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {detailTab === 'followups' && (
                    <FollowupsPanel
                      caseId={detailCase.id}
                      caseClosed={!!detailCase.file_close_date || detailCase.file_status === 'Closed'}
                    />
                  )}

                  {detailTab === 'payments' && showFinance && (
                    <>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr><th>Amount</th><th>VAT</th><th>Date</th><th>Method</th><th>Status</th></tr>
                          </thead>
                          <tbody>
                            {(detailCase.payments || []).map((p, i) => (
                              <tr key={i}>
                                <td style={{ fontWeight: 600 }}>{formatCurrency(p.amount)}</td>
                                <td>{p.vat_applied ? <span className="badge badge-blue">VAT 20%</span> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                                <td>{formatDate(p.payment_date)}</td>
                                <td>{p.payment_method || '-'}</td>
                                <td><span className={p.payment_status === 'Paid' ? 'badge badge-green' : p.payment_status === 'Partial' ? 'badge badge-yellow' : 'badge badge-red'}>{p.payment_status}</span></td>
                              </tr>
                            ))}
                            {(detailCase.payments || []).length === 0 && (
                              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20 }}>No payments recorded</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Net Amount (£)</label>
                            <input className="form-input" type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="0.00" />
                          </div>
                          <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                            <label className="form-label" style={{ visibility: 'hidden' }}>VAT</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingTop: 6 }}>
                              <input type="checkbox" checked={payVat} onChange={e => setPayVat(e.target.checked)} />
                              Add VAT (20%)
                            </label>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Date</label>
                            <input className="form-input" type="date" value={payDate} onChange={e => setPayDate(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Method</label>
                            <select className="form-input" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                              {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Status</label>
                            <select className="form-input" value={payStatus} onChange={e => setPayStatus(e.target.value)}>
                              <option>Paid</option><option>Not Paid</option><option>Partial</option>
                            </select>
                          </div>
                        </div>
                        {payVat && payAmount && (
                          <div style={{ background: 'var(--bg-light)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Net</span><span>{formatCurrency(parseFloat(payAmount) || 0)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}><span>VAT (20%)</span><span>{formatCurrency((parseFloat(payAmount) || 0) * 0.2)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border)' }}><span>Total</span><span>{formatCurrency((parseFloat(payAmount) || 0) * 1.2)}</span></div>
                          </div>
                        )}
                        <button className="btn btn-primary" onClick={handleAddPayment} disabled={savingPay || !payAmount}>
                          {savingPay ? 'Adding...' : 'Add Payment'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New/Edit Case Modal */}
      {showCaseModal && (
        <div className="modal-overlay" onClick={() => { setShowCaseModal(false); setEditingCase(null); }}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCase ? 'Edit Case' : 'New Case'}</h2>
              <button onClick={() => { setShowCaseModal(false); setEditingCase(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Client *</label>
                  <select className="form-input" value={caseForm.client_id} onChange={e => handleFormChange('client_id', e.target.value)}>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}{c.client_id ? ` (${c.client_id})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">File Reference</label>
                  <input className="form-input" type="text" placeholder="Auto-filled from client" value={caseForm.file_reference} onChange={e => handleFormChange('file_reference', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Case Type</label>
                  <select className="form-input" value={caseForm.case_type} onChange={e => handleFormChange('case_type', e.target.value)}>
                    <option value="">Select case type…</option>
                    {CASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Lead Type</label>
                  <select className="form-input" value={caseForm.lead_type} onChange={e => handleFormChange('lead_type', e.target.value)}>
                    <option value="">Select lead type…</option>
                    {LEAD_TYPES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Team</label>
                  <select className="form-input" value={caseForm.team_id} onChange={e => handleFormChange('team_id', e.target.value)}>
                    <option value="">No team</option>
                    {teams.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={caseForm.file_status} onChange={e => handleFormChange('file_status', e.target.value)}>
                    {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-input" value={caseForm.priority} onChange={e => handleFormChange('priority', e.target.value)}>
                    {PRIORITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">File Open Date</label>
                  <input className="form-input" type="date" value={caseForm.file_open_date} onChange={e => handleFormChange('file_open_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Next Deadline</label>
                  <input className="form-input" type="date" value={caseForm.next_deadline_date} onChange={e => handleFormChange('next_deadline_date', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fee (£)</label>
                  <input className="form-input" type="number" step="0.01" value={caseForm.fee} onChange={e => handleFormChange('fee', e.target.value)} />
                </div>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <label className="form-label">VAT Applicable</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input type="checkbox" checked={caseForm.vat_applicable} onChange={e => handleFormChange('vat_applicable', e.target.checked)} />
                    VAT (20%) = £{caseForm.vat_amount}
                  </label>
                </div>
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={caseForm.notes} onChange={e => handleFormChange('notes', e.target.value)} rows={2} />
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label className="form-label">Follow-up Tasks</label>
                <textarea className="form-textarea" value={caseForm.follow_up_tasks} onChange={e => handleFormChange('follow_up_tasks', e.target.value)} rows={2} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => { setShowCaseModal(false); setEditingCase(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveCase} disabled={saving || !caseForm.client_id}>{saving ? 'Saving...' : 'Save Case'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCaseId !== null && (
        <div className="modal-overlay" onClick={() => setDeletingCaseId(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Case?</h2>
              <button className="btn-icon" onClick={() => setDeletingCaseId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
                This will permanently delete the case and all its stages, payments, and data. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeletingCaseId(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--danger)', color: '#fff' }} onClick={() => handleDeleteCase(deletingCaseId)}>Delete Case</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
